/*
 * File: base-table-core.ts
 * File Created: 2023-12-19 04:54:12
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2026-01-16 12:11:32
 * Modified By: Val Liu <valuis0429@gmail.com>
 * -----
 */

import { BaseComponent } from "@root/components/base-classes";
import { EUITagTarget, IUITagResult } from "@root/core/tags";
import { IQuery, MetaAttribute, MetaParser } from "@root/core";
import { Vue, Component, Prop, Inject } from "vue-property-decorator";
import { getCellProperties } from "./core";
import { performance } from "@root/core/decorators";
import { cache } from "@root/core/decorators/cache";
import { IBaseComponentUITag, IServer } from "@root/components/interfaces";

/// make this as hidden id.
export const PRIVATE_ROW_ID = "_id";

/// the base for implement table-header & table-body & table-pagination
@Component
export class BaseTableCore extends BaseComponent {
    @Prop({ type: Object, required: true })
    query!: IQuery;

    @Prop({ type: Object, required: true })
    metas!: MetaParser;

    /// pass-in from table
    @Prop({ type: Object, required: false })
    server: IServer | undefined;

    /// inject
    @Inject({ default: undefined })
    readonly viewInstance: Vue | undefined;

    /// private helpers
    refByMetaAttribute(meta: MetaAttribute) {
        const id = `${meta.attr.name}-${meta.attr.index}`;
        let ref = this.$refs[id];
        if (Array.isArray(ref)) ref = ref[0];
        const result = { id, ref };
        return result;
    }
    // todo: measure this performance
    bindCellProperties(
        target: keyof typeof EUITagTarget,
        index,
        metaIndex,
        uiTag?: IBaseComponentUITag,
    ): IUITagResult {
        const meta = this.metas.getMetaByIndex(metaIndex);
        if (meta == null) return {}; /// it should not happen

        return getCellProperties(
            target,
            this.query,
            index,
            this.metas,
            metaIndex,
            this.viewInstance,
            uiTag,
            this.server,
            this,
            this.refByMetaAttribute(meta).ref as Vue,
        );
    }

    /// data equal
    isDataEqual(data1, data2) {
        if (data1 == null || data2 == null) return false;
        return this.getDataId(data1) === this.getDataId(data2);
    }

    /// generic handler for get id from cell data
    static getDataId(data): string | number {
        return data.id ?? data[PRIVATE_ROW_ID];
    }
    getDataId(data): string | number {
        return BaseTableCore.getDataId(data);
    }
    getHoverId(data: object | string | number): string {
        let signature = typeof data === "object" ? this.getDataId(data) : data;
        let table = this.findParentByName("Table");
        if (table == null) {
            throw `<BaseTableCore> parent table not found.`;
        }
        return `hover-${(table as any)._uid}-${signature}`;
    }
    findParentByName(name) {
        let parent = this.$parent;
        while (parent) {
            if (parent.$options.name === name) {
                return parent;
            }
            parent = parent.$parent;
        }
        return null;
    }
}
